import type {
  AttributeType,
  BoltConfiguration,
  BoltConfigurationFormFieldKey,
  FormField,
  MappingAttribute,
  ObjectTypeMapping,
} from '../types';

export const BOLT_PROFILE_OBJECT_TYPE_NAMES = ['BOLT', 'GASKET'] as const;

export type BoltProfileObjectTypeName = (typeof BOLT_PROFILE_OBJECT_TYPE_NAMES)[number];

export const BOLT_PROFILE_PCF_ATTRIBUTE_NAMES = [
  'BOLT-MODELING',
  'BOLT-SET-ACTIVE',
  'BOLT-DIA',
  'BOLT-QUANTITY',
  'BOLT-LENGTH',
  'BOLT-ITEM-DESCRIPTION',
  'BOLT-MATERIAL',
  'BOLT-STANDARD',
  'ITEM-DESCRIPTION',
  'MATERIAL',
  'STANDARD',
  'DESIGNSTD',
  'CLASS',
] as const;

export const BOLT_PROFILE_SELECTION_ATTRIBUTE_NAMES = [
  'BOLT-MODELING',
  'BOLT-SET-ACTIVE',
  'BOLT-DIA',
  'BOLT-QUANTITY',
  'BOLT-LENGTH',
  'BOLT-ITEM-DESCRIPTION',
  'BOLT-MATERIAL',
  'BOLT-STANDARD',
  'ITEM-DESCRIPTION',
  'MATERIAL',
  'STANDARD',
] as const;

export const BOLT_PROFILE_TARGET_ALIAS_ATTRIBUTE_NAMES = [
  'BOLT-MODELING',
  'ITEM-DESCRIPTION',
  'MATERIAL',
  'STANDARD',
  'DESIGNSTD',
  'CLASS',
] as const;

export interface BoltProfileMappingDefinition {
  pcfAttributeName: string;
  field: BoltConfigurationFormFieldKey;
  attributeType: AttributeType;
}

type BoltProfileMappedFieldKey = Exclude<
  BoltConfigurationFormFieldKey,
  'boltModelingChoice' | 'boltBaseFamilyValue'
>;

type BoltProfileBaseFamily = 'BoltBased' | 'GasketBased';

export const BOLT_BASED_PROFILE_MAPPING_DEFINITIONS: readonly BoltProfileMappingDefinition[] = [
  { pcfAttributeName: 'BOLT-DIA', field: 'boltDiaValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-QUANTITY', field: 'boltQuantityValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-LENGTH', field: 'boltLengthValue', attributeType: 'Parameter' },
];

export const GASKET_BASED_PROFILE_MAPPING_DEFINITIONS: readonly BoltProfileMappingDefinition[] = [
  { pcfAttributeName: 'BOLT-SET-ACTIVE', field: 'boltSetActiveValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-ITEM-DESCRIPTION', field: 'boltItemDescriptionValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-DIA', field: 'boltDiaValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-QUANTITY', field: 'boltQuantityValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-LENGTH', field: 'boltLengthValue', attributeType: 'Parameter' },
  { pcfAttributeName: 'BOLT-MATERIAL', field: 'boltMaterialValue', attributeType: 'BomColumn' },
  { pcfAttributeName: 'BOLT-STANDARD', field: 'boltStandardValue', attributeType: 'Parameter' },
];

const BOLT_PROFILE_FIELD_ATTRIBUTE_ALIASES: readonly {
  field: BoltProfileMappedFieldKey;
  pcfAttributeNames: readonly string[];
}[] = [
  { field: 'boltDiaValue', pcfAttributeNames: ['BOLT-DIA'] },
  { field: 'boltQuantityValue', pcfAttributeNames: ['BOLT-QUANTITY'] },
  { field: 'boltLengthValue', pcfAttributeNames: ['BOLT-LENGTH'] },
  { field: 'boltSetActiveValue', pcfAttributeNames: ['BOLT-SET-ACTIVE'] },
  {
    field: 'boltItemDescriptionValue',
    pcfAttributeNames: ['BOLT-ITEM-DESCRIPTION', 'ITEM-DESCRIPTION'],
  },
  { field: 'boltMaterialValue', pcfAttributeNames: ['BOLT-MATERIAL', 'MATERIAL'] },
  {
    field: 'boltStandardValue',
    pcfAttributeNames: ['BOLT-STANDARD', 'STANDARD', 'DESIGNSTD'],
  },
];

export function normalizeBoltProfileKey(value: string | null | undefined): string {
  return String(value ?? '').trim().toUpperCase().replace(/[\s_-]/g, '');
}

const toKeySet = (values: readonly string[]): Set<string> =>
  new Set(values.map((value) => normalizeBoltProfileKey(value)));

const BOLT_PROFILE_OBJECT_TYPE_KEYS = toKeySet(BOLT_PROFILE_OBJECT_TYPE_NAMES);
const BOLT_PROFILE_PCF_ATTRIBUTE_KEYS = toKeySet(BOLT_PROFILE_PCF_ATTRIBUTE_NAMES);
const BOLT_PROFILE_SELECTION_ATTRIBUTE_KEYS = toKeySet(BOLT_PROFILE_SELECTION_ATTRIBUTE_NAMES);
const BOLT_PROFILE_TARGET_ALIAS_ATTRIBUTE_KEYS = toKeySet(BOLT_PROFILE_TARGET_ALIAS_ATTRIBUTE_NAMES);

export function getBoltProfileObjectTypeName(isBoltBased: boolean): BoltProfileObjectTypeName {
  return isBoltBased ? 'BOLT' : 'GASKET';
}

export function getBoltProfileMappingDefinitions(
  isBoltBased: boolean
): readonly BoltProfileMappingDefinition[] {
  return isBoltBased
    ? BOLT_BASED_PROFILE_MAPPING_DEFINITIONS
    : GASKET_BASED_PROFILE_MAPPING_DEFINITIONS;
}

export function getBoltProfileObjectTypeAttributes(
  objectTypes: readonly ObjectTypeMapping[],
  objectTypeName: string
): MappingAttribute[] {
  const normalizedObjectTypeName = normalizeBoltProfileKey(objectTypeName);
  return (
    objectTypes.find(
      (candidate) => normalizeBoltProfileKey(candidate.name) === normalizedObjectTypeName
    )?.attributes ?? []
  );
}

export function isBoltProfileObjectTypeName(value: string | null | undefined): boolean {
  return BOLT_PROFILE_OBJECT_TYPE_KEYS.has(normalizeBoltProfileKey(value));
}

export function isBoltProfileAttributeName(value: string | null | undefined): boolean {
  return BOLT_PROFILE_PCF_ATTRIBUTE_KEYS.has(normalizeBoltProfileKey(value));
}

export function isBoltProfileSelectionAttributeName(value: string | null | undefined): boolean {
  return BOLT_PROFILE_SELECTION_ATTRIBUTE_KEYS.has(normalizeBoltProfileKey(value));
}

export function isBoltProfileTargetAliasAttributeName(value: string | null | undefined): boolean {
  return BOLT_PROFILE_TARGET_ALIAS_ATTRIBUTE_KEYS.has(normalizeBoltProfileKey(value));
}

export function isBoltProfileMappingAttribute(
  attribute: Pick<MappingAttribute, 'pcfAttributeName'>
): boolean {
  return isBoltProfileAttributeName(attribute.pcfAttributeName);
}

export function isBoltProfileMappingRow(
  objectTypeName: string | null | undefined,
  pcfAttributeName: string | null | undefined
): boolean {
  return (
    isBoltProfileObjectTypeName(objectTypeName) &&
    isBoltProfileAttributeName(pcfAttributeName)
  );
}

function getBoltProfileMappingAttributeValue(
  objectTypes: readonly ObjectTypeMapping[],
  objectTypeName: string,
  pcfAttributeNames: readonly string[]
): string | undefined {
  const normalizedAttributeNames = new Set(
    pcfAttributeNames.map((pcfAttributeName) => normalizeBoltProfileKey(pcfAttributeName))
  );
  const attribute = getBoltProfileObjectTypeAttributes(objectTypes, objectTypeName).find(
    (candidate) =>
      normalizedAttributeNames.has(normalizeBoltProfileKey(candidate.pcfAttributeName ?? ''))
  );

  if (typeof attribute?.revitParam !== 'string') {
    return undefined;
  }

  const value = attribute.revitParam.trim();
  return value ? attribute.revitParam : undefined;
}

function getFirstBoltProfileMappingAttributeValue(
  objectTypes: readonly ObjectTypeMapping[],
  objectTypeNames: readonly BoltProfileObjectTypeName[],
  pcfAttributeNames: readonly string[]
): string | undefined {
  for (const objectTypeName of objectTypeNames) {
    const value = getBoltProfileMappingAttributeValue(
      objectTypes,
      objectTypeName,
      pcfAttributeNames
    );
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
}

function hasBoltProfileSelectedAttributes(
  objectTypes: readonly ObjectTypeMapping[],
  objectTypeName: BoltProfileObjectTypeName
): boolean {
  return getBoltProfileObjectTypeAttributes(objectTypes, objectTypeName).some(
    (attribute) =>
      isBoltProfileSelectionAttributeName(attribute.pcfAttributeName) &&
      typeof attribute.revitParam === 'string' &&
      attribute.revitParam.trim().length > 0
  );
}

function withBoltProfileFormFieldValue(field: FormField, value: string): FormField {
  return { ...field, value, error: undefined };
}

function getBoltProfileFormFieldValue(field: FormField): string {
  return typeof field.value === 'string' ? field.value : '';
}

function applyBoltProfileMappedFieldValue(
  bolt: BoltConfiguration,
  field: BoltProfileMappedFieldKey,
  value: string | undefined
): BoltConfiguration {
  if (value === undefined) {
    return bolt;
  }

  return {
    ...bolt,
    [field]: withBoltProfileFormFieldValue(bolt[field], value),
  };
}

export function deriveBoltConfigurationFromProfileMappings(
  bolt: BoltConfiguration,
  objectTypes: readonly ObjectTypeMapping[]
): BoltConfiguration {
  const boltBasedValue = getBoltProfileMappingAttributeValue(
    objectTypes,
    'BOLT',
    ['BOLT-MODELING']
  );
  const gasketBasedValue = getBoltProfileMappingAttributeValue(
    objectTypes,
    'GASKET',
    ['BOLT-MODELING']
  );
  const normalizedBoltBasedValue = normalizeBoltProfileKey(boltBasedValue ?? '');
  const normalizedGasketBasedValue = normalizeBoltProfileKey(gasketBasedValue ?? '');
  const boltHasProfileMappings = hasBoltProfileSelectedAttributes(objectTypes, 'BOLT');
  const gasketHasProfileMappings = hasBoltProfileSelectedAttributes(objectTypes, 'GASKET');

  let preferredBoltCategory: BoltProfileObjectTypeName | undefined;
  let preferredBoltBaseFamily: BoltProfileBaseFamily | undefined;

  if (normalizedBoltBasedValue === 'BOLTBASED') {
    preferredBoltCategory = 'BOLT';
    preferredBoltBaseFamily = 'BoltBased';
  } else if (normalizedGasketBasedValue === 'GASKETBASED') {
    preferredBoltCategory = 'GASKET';
    preferredBoltBaseFamily = 'GasketBased';
  } else if (boltHasProfileMappings && !gasketHasProfileMappings) {
    preferredBoltCategory = 'BOLT';
    preferredBoltBaseFamily = 'BoltBased';
  } else if (gasketHasProfileMappings && !boltHasProfileMappings) {
    preferredBoltCategory = 'GASKET';
    preferredBoltBaseFamily = 'GasketBased';
  }

  const fallbackBoltCategory: BoltProfileObjectTypeName =
    preferredBoltCategory === 'BOLT' ? 'GASKET' : 'BOLT';
  const boltSearchOrder: readonly BoltProfileObjectTypeName[] = preferredBoltCategory
    ? [preferredBoltCategory, fallbackBoltCategory]
    : getBoltProfileFormFieldValue(bolt.boltModelingChoice) === 'yes'
      ? ['BOLT', 'GASKET']
      : ['GASKET', 'BOLT'];

  let resolvedBolt = bolt;
  if (preferredBoltBaseFamily) {
    const currentBaseFamilyKey = normalizeBoltProfileKey(
      getBoltProfileFormFieldValue(resolvedBolt.boltBaseFamilyValue)
    );
    const shouldRepairBaseFamily =
      !currentBaseFamilyKey ||
      currentBaseFamilyKey === 'BOLTBASED' ||
      currentBaseFamilyKey === 'GASKETBASED';

    resolvedBolt = {
      ...resolvedBolt,
      boltModelingChoice: withBoltProfileFormFieldValue(
        resolvedBolt.boltModelingChoice,
        preferredBoltBaseFamily === 'BoltBased' ? 'yes' : 'no'
      ),
      boltBaseFamilyValue: withBoltProfileFormFieldValue(
        resolvedBolt.boltBaseFamilyValue,
        shouldRepairBaseFamily
          ? preferredBoltBaseFamily
          : getBoltProfileFormFieldValue(resolvedBolt.boltBaseFamilyValue)
      ),
    };
  }

  return BOLT_PROFILE_FIELD_ATTRIBUTE_ALIASES.reduce<BoltConfiguration>(
    (nextBolt, { field, pcfAttributeNames }) =>
      applyBoltProfileMappedFieldValue(
        nextBolt,
        field,
        getFirstBoltProfileMappingAttributeValue(
          objectTypes,
          boltSearchOrder,
          pcfAttributeNames
        )
      ),
    resolvedBolt
  );
}
